<template>
  <v-dialog
    v-model="dialog"
    max-width="600"
    persistent
    :fullscreen="$vuetify.breakpoint.mobile"
  >
    <template #activator="bind">
      <slot name="activator" v-bind="bind" />
    </template>

    <card-base
      dialog
      draggable
      :title="dialogTitle"
      :error="error"
      :loading="loading"
      :disabled="loading"
      show-actions
      show-actions-cancel
      @submit="submit"
      @close="close"
      @cancel="close"
    >
      <ValidationObserver ref="observer">
        <!-- severity -->
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          :name="$t('fresh-vue-notes.fields.severity')"
        >
          <note-severity-select v-model="severity" :error-messages="errors" />
        </ValidationProvider>

        <!-- title -->
        <ValidationProvider
          v-slot="{ errors }"
          rules="max:255"
          :name="$t('fresh-vue-notes.fields.title')"
        >
          <v-text-field
            v-model="title"
            :label="$t('fresh-vue-notes.fields.title')"
            prepend-icon="$freshvuenotes-title"
            :error-messages="errors"
          />
        </ValidationProvider>

        <!-- description -->
        <ValidationProvider
          v-slot="{ errors }"
          rules="required|max:2048"
          :name="$t('fresh-vue-notes.fields.description')"
        >
          <v-textarea
            v-model="description"
            :label="$t('fresh-vue-notes.fields.description')"
            prepend-icon="$freshvuenotes-description"
            :error-messages="errors"
            counter="2048"
            no-resize
          />
        </ValidationProvider>

        <!-- files -->
        <file-dropzone
          ref="dropzone"
          v-model="dropzone"
          class="mt-2"
          allow-multiple
          :allow-image-preview="false"
          max-file-size="25MB"
          max-total-file-size="25MB"
          label-max-file-size-exceeded="Datei ist zu groß"
          label-max-file-size="Maximale Dateigröße ist {filesize}"
          label-max-total-file-size-exceeded="Maximale Gesamt-Dateigröße erreicht"
          label-max-total-file-size="Maximale Gesamt-Dateigröße ist {filesize}"
        />

        <!-- visibleOnlyForMe -->
        <v-checkbox
          v-model="visibleOnlyForMe"
          :label="$t('fresh-vue-notes.fields.private')"
        />

        <!-- send_notification -->
        <v-expand-transition>
          <v-checkbox
            v-if="showNotificationOption"
            v-model="send_notification"
            :label="$t('fresh-vue-notes.fields.send_notification')"
          />
        </v-expand-transition>
      </ValidationObserver>
    </card-base>
  </v-dialog>
</template>

<script>
import gql from 'graphql-tag'
import { S3Utils } from '@freshsolutions/fresh-vue-files'
import { FileUtils } from '@freshsolutions/fresh-vue-files'

import { UPLOAD_PRIVATE_FILE as FRAGMENT_UPLOAD_PRIVATE_FILE } from '@freshsolutions/fresh-vue-files/src/graphql/fragments.js'

import CardBase from '@freshsolutions/fresh-vue-cards/src/components/CardBase.vue' // Just import the component we need. Otherwise it won't work.
import { FileDropzone } from '@freshsolutions/fresh-vue-files'
import NoteSeveritySelect from './NoteSeveritySelect'

export default {
  name: 'NoteSaveDialog',

  components: {
    CardBase,
    FileDropzone,
    NoteSeveritySelect
  },

  props: {
    noteableId: {
      type: Number,
      required: false,
      default: null
    },

    noteableType: {
      type: String,
      required: false,
      default: null
    },

    note: {
      type: Object,
      required: false,
      default: null
    },

    enableNotifications: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      error: false,
      dialog: false,

      dropzone: [],

      title: null,
      description: null,
      visibleOnlyForMe: false, // this is the column "private". Sadly we can't use this name here, because this is a reserved keyword
      severity: 'NONE',
      send_notification: true
    }
  },

  computed: {
    showNotificationOption() {
      return this.enableNotifications && !this.visibleOnlyForMe
    },

    dialogTitle() {
      if (this.note) {
        return 'Notiz bearbeiten'
      } else {
        return 'Neue Notiz erstellen'
      }
    }
  },

  watch: {
    note: {
      handler(value) {
        this.loadFromNote(value)
      },
      immediate: true
    }
  },

  methods: {
    submit() {
      this.loading = true
      this.error = false

      this.$refs.observer.validate().then((valid) => {
        if (valid) {
          if (this.note && this.note.id) {
            // we want to update the existing note
            this.$apollo
              .mutate({
                mutation: gql`
                  mutation updateNote($id: ID!, $input: UpdateNoteInput!) {
                    updateNote(id: $id, input: $input) {
                      id

                      files {
                        ...UploadPrivateFile
                      }
                    }
                  }
                  ${FRAGMENT_UPLOAD_PRIVATE_FILE}
                `,
                variables: {
                  id: this.note.id,
                  input: {
                    private: this.visibleOnlyForMe ?? false,
                    severity: this.severity,
                    title: this.title,
                    description: this.description,

                    files: {
                      create: FileUtils.parseFilesFromDropzoneForGraphql(
                        this.dropzone,
                        'notes/files/'
                      )
                    }
                  }
                }
              })
              .then(({ data: { updateNote } }) => {
                return S3Utils.uploadFiles(updateNote.files, this.dropzone)
              })
              .then(() => {
                this.loading = false
                this.$emit('submit:success')
                this.close()
              })
              .catch(() => {
                this.loading = false
                this.error = true
                this.$emit('submit:fail')
              })
          } else if (this.noteableId && this.noteableType) {
            // we want to create a new note

            this.$apollo
              .mutate({
                mutation: gql`
                  mutation createNote($input: CreateNoteInput!) {
                    createNote(input: $input) {
                      id

                      files {
                        ...UploadPrivateFile
                      }
                    }
                  }
                  ${FRAGMENT_UPLOAD_PRIVATE_FILE}
                `,
                variables: {
                  input: {
                    private: this.visibleOnlyForMe ?? false,
                    severity: this.severity,
                    title: this.title,
                    description: this.description,
                    send_notification: !this.visibleOnlyForMe
                      ? this.send_notification
                      : false,

                    files: {
                      create: FileUtils.parseFilesFromDropzoneForGraphql(
                        this.dropzone,
                        'notes/files/'
                      )
                    },

                    noteable: {
                      connect: {
                        id: this.noteableId,
                        type: this.noteableType
                      }
                    }
                  }
                }
              })
              .then(({ data: { createNote } }) => {
                return S3Utils.uploadFiles(createNote.files, this.dropzone)
              })
              .then(() => {
                this.loading = false
                this.$emit('submit:success')
                this.close()
              })
              .catch(() => {
                this.loading = false
                this.error = true
                this.$emit('submit:fail')
              })
          } else {
            // do nothing...
            this.loading = false
          }
        } else {
          this.loading = false
          this.error = false
        }
      })
    },

    open() {
      this.dialog = true
    },

    close() {
      this.dialog = false
      this.reset()
    },

    reset() {
      this.title = null
      this.description = null
      this.visibleOnlyForMe = false
      this.severity = 'NONE'
      this.send_notification = true

      this.dropzone = []

      this.loadFromNote(this.note)

      this.loading = false
      this.error = false

      requestAnimationFrame(() => {
        if (this.$refs.observer) {
          this.$refs.observer.reset()
        }
      })
    },

    loadFromNote(note) {
      if (this.note) {
        this.title = note?.title || null
        this.description = note?.description || null
        this.visibleOnlyForMe = note?.private || null
        this.severity = note?.severity || 'NONE'
      }
    }
  }
}
</script>
